/**
 * Member launcher — 会員状態の常設パネル (右下ランチャー → 4 タブ)
 *
 * 仕様: docs/custom-member-launcher-design-spec.md
 *
 * なぜ storefront 側に要るか:
 *   新カスタマーアカウント (NEW_CUSTOMER_ACCOUNTS) で /account は別ドメインへ 302 する。
 *   theme の sections/account.liquid は描画されないので、会員状態を見せられる面がここしかない。
 *
 * 書き手を増やさない:
 *   ガレージは window.ark.garage.*、ウィッシュリストは window.ark.wishlist.* を通す。
 *   localStorage を直接書かない (cookie / vehicle-changed / wishlist-changed の伝播と
 *   サーバ同期・ロールバックは既存モジュールが持っている)。
 *
 * 4 タブで取得経路が違う。ここを混ぜない:
 *   garage   localStorage           ログイン不要
 *   wishlist localStorage + engage  任意
 *   points   App Proxy              必須 (ゲストは 401)
 *   reviews  App Proxy              必須 (ゲストは 401)
 */
(function () {
  'use strict';
  if (window.__arkMemberLauncherLoaded) return;
  window.__arkMemberLauncherLoaded = true;

  var CFG = window.ARK_MEMBER || {};
  var LOGGED_IN = CFG.loggedIn === true;
  var LOGIN_URL = CFG.loginUrl || '/account/login';
  var ROOT_URL = (CFG.rootUrl || '/').replace(/\/$/, '');
  var CART_URL = CFG.cartUrl || '/cart';

  var POINTS_BASE = (
    (window.ARK_POINTS && window.ARK_POINTS.proxyBase) ||
    '/apps/ark-engage/points'
  ).replace(/\/$/, '');
  var REVIEWS_BASE = (CFG.reviewsProxy || '/apps/ark-engage/reviews').replace(/\/$/, '');

  var VEHICLE_CHANGED = 'ark:vehicle-changed';
  var VEHICLE_REQUEST = 'ark:vehicle-change-request';
  var WISHLIST_CHANGED = 'ark:wishlist-changed';

  // 表示順。points / reviews はログイン必須で、分岐は各 render が持つ (spec §1 の対応表)
  var TABS = ['garage', 'wishlist', 'points', 'reviews'];

  var root, modal, launcher, body, footer, readout;
  var active = 'garage';
  var lastFocus = null;
  // タブごとの取得結果キャッシュ。null = 未取得、'error' = 失敗
  var cache = { points: null, reviews: null };
  // open() の先読みと render() の遅延取得が二重に走らないようにする
  var inflight = { points: false, reviews: false };

  /* ---------- helpers ---------- */

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function clear(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  function yen(n) {
    var v = Number(n);
    if (!isFinite(v)) return '';
    return '¥' + Math.round(v).toLocaleString('ja-JP');
  }

  function num(n) {
    return (Number(n) || 0).toLocaleString('ja-JP');
  }

  // 日付は必ず JST で切る。端末 TZ で切ると engage のメールと 1 日ずれる。
  var JST_YMD = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  function jstDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var p = {};
    JST_YMD.formatToParts(d).forEach(function (part) {
      p[part.type] = part.value;
    });
    return p.year + '年' + Number(p.month) + '月' + Number(p.day) + '日';
  }

  function svgIcon(paths) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '16');
    svg.setAttribute('height', '16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.75');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    paths.forEach(function (d) {
      var p = document.createElementNS(ns, 'path');
      p.setAttribute('d', d);
      svg.appendChild(p);
    });
    return svg;
  }

  function payloadOf(json) {
    if (json && typeof json === 'object' && json.data != null) return json.data;
    return json;
  }

  function getJson(url) {
    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) {
        var e = new Error('request_failed');
        e.code = res.status;
        throw e;
      }
      return res.json().then(payloadOf);
    });
  }

  /* ---------- data sources ---------- */

  function garageList() {
    return (window.ark && window.ark.garage && window.ark.garage.list()) || [];
  }

  function garageSelected() {
    return (window.ark && window.ark.garage && window.ark.garage.selected()) || null;
  }

  // entries() はキャッシュ + server 保存分の stub。list() だけ見ると別端末で
  // ♡ した分が丸ごと落ち、ドロワーと件数が食い違う
  function wishlistItems() {
    return (window.ark && window.ark.wishlist && window.ark.wishlist.entries()) || [];
  }

  // 価格は Shopify の最小単位。素の数値を ¥ に足すと 100 倍になるので、
  // 通貨対応の整形は custom-wishlist.js 側の 1 本を通す
  function wishlistPrice(item) {
    if (item.price == null) return '';
    if (window.ark && window.ark.wishlist && window.ark.wishlist.format) {
      return window.ark.wishlist.format(item.price, item.currency);
    }
    return '';
  }

  function vehicleLabel(v) {
    if (!v) return '';
    return [v.year, v.make, v.model, v.submodel].filter(Boolean).join(' ');
  }

  function sameVehicle(a, b) {
    if (!a || !b) return false;
    return (
      String(a.year) === String(b.year) &&
      a.make === b.make &&
      a.model === b.model &&
      (a.submodel || '') === (b.submodel || '')
    );
  }

  /* ---------- counts ---------- */

  function setCount(tab, text) {
    var node = root.querySelector('[data-ark-ml-count="' + tab + '"]');
    if (node) node.textContent = text;
  }

  function refreshCounts() {
    setCount('garage', String(garageList().length));
    setCount('wishlist', String(wishlistItems().length));

    if (!LOGGED_IN) {
      // ログイン必須のタブは件数を出さない (未ログインに数字を見せない)
      setCount('points', '—');
      setCount('reviews', '—');
      return;
    }
    // 対象外区分は件数も出さない (0 と出すと「使えるはずの 0pt」に読める)
    var pointsExcluded =
      (window.ARK_POINTS && window.ARK_POINTS.excluded) ||
      (cache.points && cache.points !== 'error' && cache.points.excluded);
    setCount(
      'points',
      !pointsExcluded && cache.points && cache.points !== 'error'
        ? num(cache.points.available)
        : '—'
    );
    setCount(
      'reviews',
      cache.reviews && cache.reviews !== 'error' ? String(cache.reviews.length) : '—'
    );
  }

  /* ---------- render: shared ---------- */

  function stateBlock(text, modifier) {
    var d = el('div', 'ark-ml__state' + (modifier ? ' ark-ml__state--' + modifier : ''), text);
    d.setAttribute('aria-live', 'polite');
    return d;
  }

  /** 未ログインの誘導。CTA は他タブと同じく footer に出す (本文に混ぜない)。 */
  function renderLoginPrompt(text) {
    body.appendChild(stateBlock(text));
    var a = el('a', 'ark-ml__cta', 'ログイン');
    a.href = LOGIN_URL;
    footer.appendChild(a);
  }

  function cta(label, href, ghost) {
    var node;
    if (href) {
      node = el('a', 'ark-ml__cta', label);
      node.href = href;
    } else {
      node = el('button', 'ark-ml__cta', label);
      node.type = 'button';
    }
    if (ghost) node.className += ' ark-ml__cta--ghost';
    return node;
  }

  /* ---------- render: garage ---------- */

  function renderGarage() {
    var items = garageList();
    var selected = garageSelected();

    if (!items.length) {
      body.appendChild(stateBlock('車両を登録すると、適合するパーツだけを絞り込めます。'));
    } else {
      var ul = el('ul', 'ark-ml__list');
      items.forEach(function (v) {
        var li = el('li', 'ark-ml__row');

        // 適合車の選択。ラジオ 1 択で「どの車両が基準か」を一意にする
        var label = el('label', 'ark-ml__row-select');
        var radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'ark-ml-vehicle';
        radio.checked = sameVehicle(v, selected);
        radio.addEventListener('change', function () {
          if (window.ark && window.ark.garage) window.ark.garage.select(v);
          render();
        });
        label.appendChild(radio);
        label.appendChild(el('span', null, '適合車'));
        li.appendChild(label);

        var main = el('div', 'ark-ml__row-main');
        main.appendChild(el('span', 'ark-ml__row-title', vehicleLabel(v)));
        li.appendChild(main);

        var actions = el('div', 'ark-ml__row-actions');
        var rm = el('button', 'ark-ml__row-remove');
        rm.type = 'button';
        rm.setAttribute('aria-label', vehicleLabel(v) + ' を削除');
        rm.appendChild(svgIcon(['M18 6 6 18', 'm6 6 12 12']));
        rm.addEventListener('click', function () {
          if (window.ark && window.ark.garage) window.ark.garage.remove(v);
          render();
        });
        actions.appendChild(rm);
        li.appendChild(actions);

        ul.appendChild(li);
      });
      body.appendChild(ul);
    }

    // 追加は既存の garage モーダル (年式カスケード) に委ねる。フォームを二重に作らない
    var add = el('button', 'ark-ml__add', '＋ 車両を追加');
    add.type = 'button';
    add.addEventListener('click', function () {
      close();
      window.dispatchEvent(new CustomEvent(VEHICLE_REQUEST));
    });
    body.appendChild(add);

    if (items.length) {
      footer.appendChild(cta('適合パーツを見る', ROOT_URL + '/collections/all'));
    }
  }

  /* ---------- render: wishlist ---------- */

  function renderWishlist() {
    var items = wishlistItems();
    if (!items.length) {
      body.appendChild(stateBlock('気になるパーツの ♡ をタップすると、ここに一覧できます。'));
      footer.appendChild(cta('パーツを探す', ROOT_URL + '/collections/all'));
      return;
    }

    var ul = el('ul', 'ark-ml__list');
    items.forEach(function (item) {
      var li = el('li', 'ark-ml__row');

      if (item.image) {
        var img = document.createElement('img');
        img.className = 'ark-ml__row-thumb';
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        li.appendChild(img);
      }

      var main = el('div', 'ark-ml__row-main');
      var title;
      if (item.handle) {
        title = el('a', 'ark-ml__row-title', item.title || '保存済みアイテム');
        title.href = ROOT_URL + '/products/' + encodeURIComponent(item.handle);
      } else {
        title = el('span', 'ark-ml__row-title', item.title || '保存済みアイテム');
      }
      main.appendChild(title);
      var priceText = wishlistPrice(item);
      if (priceText) main.appendChild(el('span', 'ark-ml__row-sub', priceText));
      li.appendChild(main);

      var actions = el('div', 'ark-ml__row-actions');

      if (item.variantId) {
        var add = el('button', 'ark-ml__row-link', 'カートに入れる');
        add.type = 'button';
        add.addEventListener('click', function () {
          addToCart(item.variantId, add);
        });
        actions.appendChild(add);
      }

      var rm = el('button', 'ark-ml__row-remove');
      rm.type = 'button';
      rm.setAttribute('aria-label', (item.title || 'アイテム') + ' を削除');
      rm.appendChild(svgIcon(['M18 6 6 18', 'm6 6 12 12']));
      rm.addEventListener('click', function () {
        // 楽観更新・サーバ同期・失敗時ロールバックは custom-wishlist.js が持つ
        if (window.ark && window.ark.wishlist) window.ark.wishlist.remove(item.variantId);
        render();
      });
      actions.appendChild(rm);

      li.appendChild(actions);
      ul.appendChild(li);
    });
    body.appendChild(ul);
    footer.appendChild(cta('カートを見る', CART_URL, true));
  }

  function addToCart(variantId, btn) {
    btn.disabled = true;
    btn.textContent = '追加中…';
    fetch(ROOT_URL + '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (res) {
        btn.disabled = false;
        // 在庫切れ・variant 不正はここに来る。文言で理由を潰さず、状態だけ返す
        btn.textContent = res.ok ? '追加しました' : '追加できません';
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = '追加できません';
      });
  }

  /* ---------- render: points ---------- */

  // 交換ルール (下限 / 刻み / 段階レート) は engage の balance API が配る。theme 側で
  // 数値を持たない = ルール変更でテーマを再デプロイしなくてよい。旧レスポンス互換のため
  // rules 欠落時は倍率の話をせず、事実だけ書く。
  function pointsRateNote(rules) {
    if (!rules) return '1 pt = 1 円';
    return num(rules.stepPt) + 'pt 単位・' + num(rules.minPt) + 'pt から交換できます';
  }

  // 倍率は必ず小数 1 桁。×1 と ×1.1 が並ぶと桁が揃わず、等幅の計器表示が崩れる。
  function mult(v) {
    return '×' + Number(v).toFixed(1);
  }

  // レート表。まとめて使うほど 1pt の価値が上がることを「読ませる」ための計器表示で、
  // ここ自体は押せない (spec §6: 押せる色は CTA 1 つに絞る)。
  // parent は .ark-ml__points (padding 持ち) を渡すこと。body 直下に置くと左端に張り付く。
  function appendRateLadder(parent, rules) {
    if (!rules || !Array.isArray(rules.tiers) || rules.tiers.length < 2) return;

    var label = el('p', 'ark-ml__points-label', '交換レート');
    label.className += ' ark-ml__points-label--sub';
    parent.appendChild(label);

    var list = el('div', 'ark-ml__rates');
    rules.tiers.forEach(function (t) {
      var row = el('div', 'ark-ml__rate');
      if (Number(t.multiplier) === Number(rules.currentMultiplier)) {
        row.className += ' ark-ml__rate--current';
      }
      // 下限未満は倍率以前に交換できないので、最下段は 0pt でなく下限から書く
      var from = Math.max(Number(t.minPt) || 0, Number(rules.minPt) || 0);
      row.appendChild(el('span', 'ark-ml__rate-from', num(from) + 'pt 〜'));
      row.appendChild(el('span', 'ark-ml__rate-mult', mult(t.multiplier)));
      list.appendChild(row);
    });
    parent.appendChild(list);
  }

  // 「あと N pt で倍率が上がる」。動機づけの一文なので残高の直下に置く。レート表と一緒に
  // 下へ流すと 560px 固定の折り返し外に落ちて読まれない。
  function rateProgressText(rules) {
    if (!rules || !rules.nextTier) return null;
    return (
      'あと ' +
      num(rules.nextTier.shortfall) +
      'pt で ' +
      mult(rules.nextTier.multiplier) +
      '（' +
      num(rules.nextTier.minPt) +
      'pt が ' +
      yen(rules.nextTier.minPt * rules.nextTier.multiplier) +
      ' 分になります）'
    );
  }

  function renderPoints() {
    if (!LOGGED_IN) {
      renderLoginPrompt('ログインするとポイント残高を確認できます。');
      return;
    }
    // 対象外区分は残高を取りに行かない (API も { excluded:true } しか返さない)
    if (window.ARK_POINTS && window.ARK_POINTS.excluded) {
      cache.points = { excluded: true };
    }
    if (cache.points === null) {
      body.appendChild(stateBlock('読み込み中…'));
      fetchPoints();
      return;
    }
    if (cache.points === 'error') {
      body.appendChild(stateBlock('ポイント残高を取得できませんでした。', 'error'));
      var retry = cta('再読み込み', null, true);
      retry.addEventListener('click', function () {
        cache.points = null;
        render();
      });
      footer.appendChild(retry);
      return;
    }

    var b = cache.points;

    // 対象外区分 (業販 / B2B / アンバサダー)。残高そのものを持たせず、理由だけ出す。
    // API は { excluded:true } を返す。Liquid の window.ARK_POINTS.excluded は初回描画用。
    if ((b && b.excluded) || (window.ARK_POINTS && window.ARK_POINTS.excluded)) {
      var note = el('div', 'ark-ml__points');
      note.appendChild(el('p', 'ark-ml__points-label', 'ポイント'));
      note.appendChild(
        el(
          'p',
          'ark-ml__points-note',
          'このアカウントはポイント対象外です（割引価格でのご購入のため）。'
        )
      );
      body.appendChild(note);
      return;
    }

    var box = el('div', 'ark-ml__points');
    box.appendChild(el('p', 'ark-ml__points-label', '利用可能ポイント'));
    var readoutLine = document.createElement('div');
    readoutLine.appendChild(el('span', 'ark-ml__points-value', num(b.available)));
    readoutLine.appendChild(el('span', 'ark-ml__points-unit', 'pt'));
    box.appendChild(readoutLine);
    box.appendChild(el('p', 'ark-ml__points-note', pointsRateNote(b.redeemRules)));
    var progress = rateProgressText(b.redeemRules);
    if (progress) {
      box.appendChild(el('p', 'ark-ml__points-note ark-ml__points-note--lead', progress));
    }
    if (b.pending > 0) {
      box.appendChild(
        el('p', 'ark-ml__points-note', num(b.pending) + 'pt は返品確定後に利用可能になります')
      );
    }
    if (b.expiringSoon && b.expiringSoon.points > 0) {
      box.appendChild(
        el(
          'p',
          'ark-ml__points-note',
          num(b.expiringSoon.points) + 'pt が ' + jstDate(b.expiringSoon.date) + ' に失効します'
        )
      );
    }
    appendRateLadder(box, b.redeemRules);
    body.appendChild(box);

    // 発行済みコードはここが取り戻し口。モーダルを閉じても失われないことが要件
    var codes = Array.isArray(b.activeRedemptions) ? b.activeRedemptions : [];
    if (codes.length) {
      var codeLabel = el('p', 'ark-ml__points-label', '発行済みクーポン');
      codeLabel.className += ' ark-ml__points-label--loose';
      body.appendChild(codeLabel);
      codes.forEach(function (c) {
        var box2 = el('div', 'ark-ml__coupon');
        box2.appendChild(el('span', 'ark-ml__coupon-code', c.code));
        // 段階レートがあるので pt と円は 1:1 でない。発行済みコードでも消費 pt を併記して
        // 「1,000pt が ¥1,100 になった」ことが後から読めるようにする。
        var spent = Number(c.pointsSpent);
        var amount =
          isFinite(spent) && spent > 0
            ? num(spent) + 'pt → ' + yen(c.discountValueYen) + ' 割引'
            : yen(c.discountValueYen) + ' 割引';
        box2.appendChild(
          el('span', 'ark-ml__coupon-meta', amount + '・' + jstDate(c.expiresAt) + 'まで有効')
        );
        var copy = el('button', 'ark-ml__row-link', 'コードをコピー');
        copy.type = 'button';
        copy.addEventListener('click', function () {
          copyText(c.code, copy);
        });
        box2.appendChild(copy);
        body.appendChild(box2);
      });
    }

    footer.appendChild(cta('カートでポイントを使う', CART_URL));
  }

  function fetchPoints() {
    if (inflight.points) return;
    inflight.points = true;
    getJson(POINTS_BASE + '/balance')
      .then(function (data) {
        cache.points = data || {};
      })
      .catch(function () {
        cache.points = 'error';
      })
      .then(function () {
        inflight.points = false;
        refreshCounts();
        if (active === 'points' && !modal.hidden) render();
      });
  }

  function copyText(text, btn) {
    var done = function (ok) {
      btn.textContent = ok ? 'コピーしました' : 'コピーできません';
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(
        function () {
          done(true);
        },
        function () {
          done(false);
        }
      );
      return;
    }
    // clipboard API が無い環境。成功を偽らない
    done(false);
  }

  /* ---------- render: reviews ---------- */

  function renderReviews() {
    if (!LOGGED_IN) {
      renderLoginPrompt('ログインすると、レビュー待ちの商品を確認できます。');
      return;
    }
    if (cache.reviews === null) {
      body.appendChild(stateBlock('読み込み中…'));
      fetchReviews();
      return;
    }
    if (cache.reviews === 'error') {
      body.appendChild(stateBlock('レビュー待ちを取得できませんでした。', 'error'));
      var retry = cta('再読み込み', null, true);
      retry.addEventListener('click', function () {
        cache.reviews = null;
        render();
      });
      footer.appendChild(retry);
      return;
    }
    if (!cache.reviews.length) {
      body.appendChild(stateBlock('レビュー待ちの商品はありません。'));
      return;
    }

    var ul = el('ul', 'ark-ml__list');
    cache.reviews.forEach(function (item) {
      var li = el('li', 'ark-ml__row');
      var main = el('div', 'ark-ml__row-main');
      // productTitle は engage が記録時点の名前を返す。null なら作り話をしない
      var label = item.productTitle || '購入した商品';
      // 投稿は商品ページのレビュー欄で行う。handle が解決できた商品だけリンクにし、
      // #custom-reviews まで送って投稿フォームの位置に着地させる (theme 側の id と一致させる)
      var title;
      if (item.productHandle) {
        title = el('a', 'ark-ml__row-title', label);
        title.href =
          ROOT_URL + '/products/' + encodeURIComponent(item.productHandle) + '#custom-reviews';
      } else {
        title = el('span', 'ark-ml__row-title', label);
      }
      main.appendChild(title);
      main.appendChild(
        el('span', 'ark-ml__row-sub', jstDate(item.eligibleAt) + ' から投稿できます')
      );
      li.appendChild(main);

      if (item.productHandle) {
        var actions = el('div', 'ark-ml__row-actions');
        var write = el('a', 'ark-ml__row-link', 'レビューを書く');
        write.href = title.href;
        actions.appendChild(write);
        li.appendChild(actions);
      }

      ul.appendChild(li);
    });
    body.appendChild(ul);
  }

  function fetchReviews() {
    if (inflight.reviews) return;
    inflight.reviews = true;
    getJson(REVIEWS_BASE + '/pending')
      .then(function (data) {
        cache.reviews = data && Array.isArray(data.items) ? data.items : [];
      })
      .catch(function () {
        cache.reviews = 'error';
      })
      .then(function () {
        inflight.reviews = false;
        refreshCounts();
        if (active === 'reviews' && !modal.hidden) render();
      });
  }

  /* ---------- render ---------- */

  function render() {
    clear(body);
    clear(footer);
    refreshCounts();

    TABS.forEach(function (t) {
      var btn = root.querySelector('[data-ark-ml-tab="' + t + '"]');
      if (btn) btn.setAttribute('aria-selected', String(t === active));
    });

    if (active === 'garage') renderGarage();
    else if (active === 'wishlist') renderWishlist();
    else if (active === 'points') renderPoints();
    else renderReviews();

    var closeBtn = cta('閉じる', null, true);
    closeBtn.addEventListener('click', close);
    footer.appendChild(closeBtn);
  }

  /* ---------- open / close ---------- */

  function focusable() {
    return modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function open() {
    lastFocus = document.activeElement;
    modal.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    // 開いた時点で 4 タブぶんの件数を出す。タブを踏むまで取りに行かないと、
    // 開いた直後だけ 2 つが「—」になり、0 件なのか未取得なのか読めない
    if (LOGGED_IN) {
      if (cache.points === null) fetchPoints();
      if (cache.reviews === null) fetchReviews();
    }
    render();
    // garage / wishlist と同じ共有ロック。スクロールバー幅を padding で
    // 埋め戻すのでページはずれない
    if (window.__arkScrollLock) window.__arkScrollLock.lock();
    var f = focusable();
    if (f.length) f[0].focus();
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    if (modal.hidden) return; // 二重 unlock でロックカウントを壊さない
    modal.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    if (window.__arkScrollLock) window.__arkScrollLock.unlock();
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusable();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- boot ---------- */

  function boot() {
    root = document.querySelector('[data-ark-ml]');
    if (!root) return;
    modal = root.querySelector('[data-ark-ml-modal]');
    launcher = root.querySelector('[data-ark-ml-launcher]');
    body = root.querySelector('[data-ark-ml-body]');
    footer = root.querySelector('[data-ark-ml-footer]');
    readout = root.querySelector('[data-ark-ml-readout]');

    if (LOGGED_IN && CFG.name) {
      readout.hidden = false;
      root.querySelector('[data-ark-ml-name]').textContent = CFG.name;
      root.querySelector('[data-ark-ml-id]').textContent =
        CFG.id != null ? '会員ID : ' + CFG.id : '';
    }

    launcher.addEventListener('click', function () {
      if (modal.hidden) open();
      else close();
    });
    root.querySelector('[data-ark-ml-close]').addEventListener('click', close);
    root.querySelector('[data-ark-ml-backdrop]').addEventListener('click', close);

    TABS.forEach(function (t) {
      var btn = root.querySelector('[data-ark-ml-tab="' + t + '"]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        active = t;
        render();
      });
    });

    // 他 widget での変更を取り込む (♡ トグル / garage モーダル)
    window.addEventListener(WISHLIST_CHANGED, function () {
      if (!modal.hidden) render();
      else refreshCounts();
    });
    window.addEventListener(VEHICLE_CHANGED, function () {
      if (!modal.hidden) render();
      else refreshCounts();
    });

    refreshCounts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
